import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const TARGET_GAIN = 0.05;

export const AmbientAudio = () => {
  const [on, setOn] = useState(false);
  const engineRef = useRef(null);

  const build = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);

    const droneGain = ctx.createGain();
    droneGain.gain.value = 1;
    const o1 = ctx.createOscillator();
    o1.type = "sine";
    o1.frequency.value = 55;
    const g1 = ctx.createGain();
    g1.gain.value = 0.5;
    const o2 = ctx.createOscillator();
    o2.type = "sine";
    o2.frequency.value = 110.4;
    const g2 = ctx.createGain();
    g2.gain.value = 0.22;
    o1.connect(g1);
    o2.connect(g2);
    g1.connect(droneGain);
    g2.connect(droneGain);
    droneGain.connect(master);

    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 900;
    filter.Q.value = 0.8;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.12;
    noise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(master);

    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.08;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = TARGET_GAIN * 0.3;
    lfo.connect(lfoGain);
    lfoGain.connect(master.gain);

    o1.start();
    o2.start();
    noise.start();
    lfo.start();
    return { ctx, master, sources: [o1, o2, noise, lfo] };
  };

  const toggle = async () => {
    if (!on) {
      if (!engineRef.current) engineRef.current = build();
      engineRef.current.enabled = true;
      const { ctx, master } = engineRef.current;
      if (ctx.state === "suspended") await ctx.resume();
      master.gain.cancelScheduledValues(ctx.currentTime);
      master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
      master.gain.linearRampToValueAtTime(TARGET_GAIN, ctx.currentTime + 1.8);
      setOn(true);
    } else {
      const { ctx, master } = engineRef.current;
      engineRef.current.enabled = false;
      master.gain.cancelScheduledValues(ctx.currentTime);
      master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
      master.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.6);
      setOn(false);
      setTimeout(() => {
        if (engineRef.current && engineRef.current.enabled === false) engineRef.current.ctx.suspend();
      }, 700);
    }
  };

  useEffect(
    () => () => {
      if (engineRef.current) {
        engineRef.current.sources.forEach((s) => {
          try { s.stop(); } catch (e) {}
        });
        engineRef.current.ctx.close();
        engineRef.current = null;
      }
    },
    []
  );

  return (
    <button
      onClick={toggle}
      aria-pressed={on}
      aria-label={on ? "Turn ambient sound off" : "Turn ambient sound on"}
      data-testid="ambient-audio-toggle"
      className="group absolute bottom-6 right-5 z-20 flex items-center gap-2.5 rounded-full border hairline glass-panel px-3.5 py-2 text-muted-foreground transition-colors duration-200 hover:border-primary/40 hover:text-primary sm:right-8"
    >
      {on ? <Volume2 className="h-3.5 w-3.5 text-primary" /> : <VolumeX className="h-3.5 w-3.5" />}
      <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{on ? "Sound on" : "Sound"}</span>
      {on && (
        <span className="flex h-3 items-end gap-[2px]" aria-hidden="true">
          <span className="eq-bar h-full w-[2px] rounded-full bg-primary" style={{ animationDelay: "0s" }} />
          <span className="eq-bar h-full w-[2px] rounded-full bg-primary" style={{ animationDelay: "0.25s" }} />
          <span className="eq-bar h-full w-[2px] rounded-full bg-primary" style={{ animationDelay: "0.5s" }} />
        </span>
      )}
    </button>
  );
};
